import {
  parseMarkdownBlocks,
  serializeMarkdownBlocks,
  type MarkdownBlock,
  type MarkdownSpan,
} from '@aysar/contracts';
import { type JSONContent } from '@tiptap/core';

/**
 * The legal-body grammar (`@aysar/contracts` `body-markdown`) ⇄ the editor's
 * document. The form holds the markdown string; the editor holds a ProseMirror
 * document; these two functions are the whole boundary between them.
 */

const BOLD_MARK = { type: 'bold' };

function spansToContent(spans: readonly MarkdownSpan[]): JSONContent[] {
  return spans
    .filter((span) => span.text !== '')
    .map((span) => ({
      type: 'text',
      text: span.text,
      ...(span.isBold ? { marks: [BOLD_MARK] } : {}),
    }));
}

function textBlock(type: 'heading' | 'paragraph', spans: readonly MarkdownSpan[], level?: number) {
  const content = spansToContent(spans);
  return {
    type,
    ...(level === undefined ? {} : { attrs: { level } }),
    ...(content.length === 0 ? {} : { content }),
  } satisfies JSONContent;
}

function listBlock(type: 'bulletList' | 'orderedList', items: readonly MarkdownSpan[][]) {
  return {
    type,
    content: items.map((item) => ({
      type: 'listItem',
      content: [textBlock('paragraph', item)],
    })),
  } satisfies JSONContent;
}

export function markdownToDoc(body: string): JSONContent {
  const content = parseMarkdownBlocks(body).map((block) => {
    switch (block.kind) {
      case 'heading':
        return textBlock('heading', block.spans, block.level);
      case 'paragraph':
        return textBlock('paragraph', block.spans);
      case 'list':
        return listBlock('bulletList', block.items);
      case 'ordered-list':
        return listBlock('orderedList', block.items);
    }
  });

  // ProseMirror refuses an empty doc; one empty paragraph is "nothing yet".
  return { type: 'doc', content: content.length === 0 ? [{ type: 'paragraph' }] : content };
}

function contentToSpans(nodes: readonly JSONContent[] | undefined): MarkdownSpan[] {
  const spans: MarkdownSpan[] = [];

  for (const node of nodes ?? []) {
    if (node.type === 'text' && typeof node.text === 'string') {
      spans.push({
        text: node.text,
        isBold: (node.marks ?? []).some((mark) => mark.type === 'bold'),
      });
    } else if (node.content !== undefined) {
      spans.push(...contentToSpans(node.content));
    }
  }

  return spans;
}

/** The text of a list item: its paragraphs, joined the way a hard wrap is. */
function itemSpans(item: JSONContent): MarkdownSpan[] {
  const paragraphs = (item.content ?? []).map((node) => contentToSpans(node.content));
  const joined: MarkdownSpan[] = [];

  paragraphs.forEach((spans, index) => {
    if (index > 0 && spans.length > 0 && joined.length > 0) {
      joined.push({ text: ' ', isBold: false });
    }
    joined.push(...spans);
  });

  return joined;
}

function isBlank(spans: readonly MarkdownSpan[]): boolean {
  return spans.every((span) => span.text.trim() === '');
}

export function docToBlocks(doc: JSONContent): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];

  for (const node of doc.content ?? []) {
    switch (node.type) {
      case 'heading': {
        const spans = contentToSpans(node.content);
        if (isBlank(spans)) break;
        const raw = typeof node.attrs?.['level'] === 'number' ? node.attrs['level'] : 1;
        blocks.push({ kind: 'heading', level: clampLevel(raw), spans });
        break;
      }
      case 'bulletList':
      case 'orderedList': {
        const items = (node.content ?? []).map(itemSpans).filter((spans) => !isBlank(spans));
        if (items.length === 0) break;
        blocks.push({ kind: node.type === 'bulletList' ? 'list' : 'ordered-list', items });
        break;
      }
      default: {
        // A paragraph, or anything the schema let through: its text is a paragraph.
        const spans = contentToSpans(node.content);
        if (!isBlank(spans)) blocks.push({ kind: 'paragraph', spans });
      }
    }
  }

  return blocks;
}

function clampLevel(level: number): 1 | 2 | 3 {
  return level <= 1 ? 1 : level === 2 ? 2 : 3;
}

export function docToMarkdown(doc: JSONContent): string {
  return serializeMarkdownBlocks(docToBlocks(doc));
}

/**
 * Whether what the editor shows survives a save and a reload.
 *
 * The grammar has no escaping, so a paragraph typed as `- foo` or `1. foo`
 * comes back as a list. The editor cannot stop that without inventing syntax
 * the mobile reader does not know; it can say so.
 */
export function hasMarkdownDrift(blocks: readonly MarkdownBlock[]): boolean {
  const reparsed = parseMarkdownBlocks(serializeMarkdownBlocks(blocks));
  return JSON.stringify(reparsed) !== JSON.stringify(blocks.map(normalizeBlock));
}

/**
 * What the parser would hand back for the same text: adjacent same-style spans
 * merged, and the line's ends trimmed — otherwise a trailing space mid-typing
 * would read as drift.
 */
function normalizeBlock(block: MarkdownBlock): MarkdownBlock {
  const merge = (spans: readonly MarkdownSpan[]): MarkdownSpan[] => {
    const out: MarkdownSpan[] = [];
    for (const span of spans) {
      if (span.text === '') continue;
      const last = out[out.length - 1];
      if (last !== undefined && last.isBold === span.isBold) last.text += span.text;
      else out.push({ ...span });
    }

    const first = out[0];
    if (first !== undefined) first.text = first.text.trimStart();
    const end = out[out.length - 1];
    if (end !== undefined) end.text = end.text.trimEnd();

    return out.filter((span) => span.text !== '');
  };

  switch (block.kind) {
    case 'heading':
      return { ...block, spans: merge(block.spans) };
    case 'paragraph':
      return { ...block, spans: merge(block.spans) };
    case 'list':
    case 'ordered-list':
      return { ...block, items: block.items.map(merge) };
  }
}
